import React, { useEffect, useMemo, useState } from 'react';
import RowDetailsSheet from './RowDetailsSheet';

export const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    setIsMobile(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, [breakpoint]);
  return isMobile;
};

const compareValues = (a, b, dir) => {
  if (a == null && b == null) return 0;
  if (a == null) return dir === 'asc' ? -1 : 1;
  if (b == null) return dir === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') {
    return dir === 'asc' ? a - b : b - a;
  }
  const cmp = String(a).localeCompare(String(b), undefined, { numeric: true });
  return dir === 'asc' ? cmp : -cmp;
};

const ReportSummaryCards = ({ stats }) => (
  <div className="mrv-summary">
    {stats.map((s) => (
      <div key={s.label} className="mrv-stat">
        <div className="mrv-stat-label">{s.label}</div>
        <div className="mrv-stat-value">{s.value}</div>
      </div>
    ))}
  </div>
);

const ReportFilterBar = ({
  search, onSearchChange,
  sortOptions, sortBy, onSortByChange, sortDir, onSortDirChange,
  facets, activeFacets, onFacetChange,
  hideUncounted,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const facetEntries = facets ? Object.entries(facets) : [];
  const activeFacetCount = facetEntries.reduce((acc, [key]) => acc + (activeFacets[key] ? 1 : 0), 0);
  return (
    <div className="mrv-filterbar">
      <div className="mrv-filterbar-row">
        <input
          type="search"
          className="mrv-search"
          placeholder="Search…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        {(facetEntries.length > 0) && (
          <button
            type="button"
            className={`mrv-pill ${activeFacetCount > 0 || showFilters ? 'is-active' : ''}`}
            onClick={() => setShowFilters((v) => !v)}
          >
            Filters{activeFacetCount > 0 ? ` (${activeFacetCount})` : ''}
          </button>
        )}
      </div>
      <div className="mrv-filterbar-row">
        {sortOptions && sortOptions.length > 0 && (
          <>
            <select
              className="mrv-select"
              value={sortBy}
              onChange={(e) => onSortByChange(e.target.value)}
              aria-label="Sort by"
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>Sort: {opt.label}</option>
              ))}
            </select>
            <button
              type="button"
              className="mrv-dir"
              onClick={() => onSortDirChange(sortDir === 'asc' ? 'desc' : 'asc')}
              aria-label="Toggle sort direction"
              title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
            >
              {sortDir === 'asc' ? '↑' : '↓'}
            </button>
          </>
        )}
        {hideUncounted}
      </div>
      {showFilters && facetEntries.length > 0 && (
        <div className="mrv-facets">
          {facetEntries.map(([key, options]) => (
            <div key={key} className="mrv-facet">
              <label className="mrv-facet-label">{key}</label>
              <select
                className="mrv-select"
                value={activeFacets[key] || ''}
                onChange={(e) => onFacetChange(key, e.target.value)}
              >
                <option value="">All {key.toLowerCase()}</option>
                {options.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          ))}
          {activeFacetCount > 0 && (
            <button
              type="button"
              className="mrv-clear"
              onClick={() => facetEntries.forEach(([k]) => onFacetChange(k, ''))}
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ReportItemCard = ({ item, onClick }) => (
  <button type="button" className="mrv-card" onClick={onClick}>
    <div className="mrv-card-main">
      <div className="mrv-card-title">{item.title}</div>
      {item.subtitle && <div className="mrv-card-sub">{item.subtitle}</div>}
      {item.tags && item.tags.length > 0 && (
        <div className="mrv-card-tags">
          {item.tags.map((t, i) => (
            <span key={`${t}-${i}`} className="mrv-tag">{t}</span>
          ))}
        </div>
      )}
    </div>
    <div className="mrv-card-metrics">
      {item.primaryMetric && (
        <div className="mrv-metric mrv-metric-primary">
          <div className="mrv-metric-value">{item.primaryMetric.value}</div>
          <div className="mrv-metric-label">{item.primaryMetric.label}</div>
        </div>
      )}
      {item.secondaryMetric && (
        <div className="mrv-metric">
          <div className="mrv-metric-value">{item.secondaryMetric.value}</div>
          <div className="mrv-metric-label">{item.secondaryMetric.label}</div>
        </div>
      )}
    </div>
    <div className="mrv-card-chev" aria-hidden="true">›</div>
  </button>
);

const ReportItemList = ({ items, onItemClick, groupBy }) => {
  if (!items.length) {
    return <div className="mrv-empty">No items match the current filters.</div>;
  }
  if (!groupBy) {
    return (
      <div className="mrv-list">
        {items.map((item, idx) => (
          <ReportItemCard key={item.id || idx} item={item} onClick={() => onItemClick(item)} />
        ))}
      </div>
    );
  }
  const groups = items.reduce((acc, item) => {
    const key = item.group || 'Other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
  return (
    <div className="mrv-list">
      {Object.entries(groups).map(([groupName, groupItems]) => (
        <div key={groupName} className="mrv-group">
          <div className="mrv-group-header">{groupName}</div>
          {groupItems.map((item, idx) => (
            <ReportItemCard key={item.id || `${groupName}-${idx}`} item={item} onClick={() => onItemClick(item)} />
          ))}
        </div>
      ))}
    </div>
  );
};

const MobileReportView = ({
  stats,
  items,
  sortOptions,
  defaultSortBy,
  defaultSortDir = 'desc',
  facetKeys,
  hideUncountedToggle, // { checked, onChange, label }
  groupBy,
  detailActions,
  emptyMessage,
}) => {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState(defaultSortBy || (sortOptions && sortOptions[0]?.value) || '');
  const [sortDir, setSortDir] = useState(defaultSortDir);
  const [activeFacets, setActiveFacets] = useState({});
  const [activeItem, setActiveItem] = useState(null);

  const facets = useMemo(() => {
    if (!facetKeys || !facetKeys.length) return null;
    const out = {};
    facetKeys.forEach((key) => {
      const set = new Set();
      items.forEach((it) => {
        const v = it.facets ? it.facets[key] : null;
        if (v != null && v !== '') set.add(String(v));
      });
      if (set.size > 0) out[key] = Array.from(set).sort();
    });
    return Object.keys(out).length ? out : null;
  }, [items, facetKeys]);

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = items.filter((it) => {
      if (q) {
        const hay = [
          it.title, it.subtitle,
          it.primaryMetric?.value, it.secondaryMetric?.value,
          ...(it.tags || []),
          ...(it.facets ? Object.values(it.facets) : []),
        ].filter(Boolean).map((v) => String(v).toLowerCase()).join(' ');
        if (!hay.includes(q)) return false;
      }
      for (const [key, val] of Object.entries(activeFacets)) {
        if (!val) continue;
        if (String(it.facets?.[key] || '') !== String(val)) return false;
      }
      return true;
    });
    if (sortBy) {
      result = [...result].sort((a, b) => compareValues(a.sortValues?.[sortBy], b.sortValues?.[sortBy], sortDir));
    }
    return result;
  }, [items, search, sortBy, sortDir, activeFacets]);

  return (
    <div className="mrv">
      {stats && stats.length > 0 && <ReportSummaryCards stats={stats} />}

      <ReportFilterBar
        search={search}
        onSearchChange={setSearch}
        sortOptions={sortOptions}
        sortBy={sortBy}
        onSortByChange={setSortBy}
        sortDir={sortDir}
        onSortDirChange={setSortDir}
        facets={facets}
        activeFacets={activeFacets}
        onFacetChange={(key, value) => setActiveFacets((prev) => {
          const next = { ...prev };
          if (!value) delete next[key]; else next[key] = value;
          return next;
        })}
        hideUncounted={hideUncountedToggle ? (
          <label className="mrv-hideuncounted">
            <input
              type="checkbox"
              checked={hideUncountedToggle.checked}
              onChange={(e) => hideUncountedToggle.onChange(e.target.checked)}
            />
            <span>{hideUncountedToggle.label || 'Hide items with no count'}</span>
          </label>
        ) : null}
      />

      <div className="mrv-resultcount">
        Showing {filteredSorted.length} of {items.length}
      </div>

      <ReportItemList
        items={filteredSorted}
        onItemClick={setActiveItem}
        groupBy={groupBy}
      />

      {emptyMessage && filteredSorted.length === 0 && items.length === 0 && (
        <div className="mrv-empty">{emptyMessage}</div>
      )}

      {activeItem && (
        <RowDetailsSheet
          title={activeItem.title}
          fields={activeItem.detailFields || []}
          actions={detailActions ? detailActions(activeItem) : null}
          onClose={() => setActiveItem(null)}
        />
      )}

      <style>{`
        .mrv { padding: 0 4px 80px; }
        .mrv-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 8px;
          margin: 8px 0 12px;
        }
        .mrv-stat {
          background: #fff;
          border: 1px solid #e8e0d8;
          border-radius: 10px;
          padding: 10px 12px;
          text-align: left;
        }
        .mrv-stat-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #8b7b73;
          margin-bottom: 4px;
        }
        .mrv-stat-value {
          font-size: 1.05rem;
          font-weight: 700;
          color: #3D2518;
          line-height: 1.2;
        }
        .mrv-filterbar {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 10px;
          position: sticky;
          top: 0;
          z-index: 5;
          background: #f6efe8;
          padding: 8px 0;
        }
        .mrv-filterbar-row {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .mrv-search {
          flex: 1;
          min-width: 0;
          padding: 9px 12px;
          font-size: 0.9rem;
          border: 1px solid #d6ccc1;
          border-radius: 8px;
          background: #fff;
          color: #3D2518;
        }
        .mrv-select {
          padding: 9px 10px;
          font-size: 0.85rem;
          border: 1px solid #d6ccc1;
          border-radius: 8px;
          background: #fff;
          color: #3D2518;
          flex: 1;
          min-width: 0;
        }
        .mrv-pill {
          padding: 9px 14px;
          font-size: 0.85rem;
          border: 1px solid #d6ccc1;
          border-radius: 999px;
          background: #fff;
          color: #5A4A42;
          white-space: nowrap;
        }
        .mrv-pill.is-active {
          background: #5A4A42;
          color: #fff;
          border-color: #5A4A42;
        }
        .mrv-dir {
          padding: 9px 14px;
          font-size: 1rem;
          border: 1px solid #d6ccc1;
          border-radius: 8px;
          background: #fff;
          color: #3D2518;
          font-weight: 700;
        }
        .mrv-hideuncounted {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.8rem;
          color: #5A4A42;
          margin-left: auto;
        }
        .mrv-hideuncounted input { accent-color: #8B6F5E; }
        .mrv-facets {
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 10px;
          background: #fff;
          border: 1px solid #e8e0d8;
          border-radius: 10px;
        }
        .mrv-facet { display: flex; flex-direction: column; gap: 4px; }
        .mrv-facet-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #8b7b73;
        }
        .mrv-clear {
          align-self: flex-start;
          background: none;
          border: none;
          color: #8B6F5E;
          font-size: 0.85rem;
          text-decoration: underline;
          padding: 4px 0;
        }
        .mrv-resultcount {
          font-size: 0.75rem;
          color: #8b7b73;
          margin: 4px 4px 8px;
        }
        .mrv-list { display: flex; flex-direction: column; gap: 8px; }
        .mrv-group { display: flex; flex-direction: column; gap: 6px; }
        .mrv-group-header {
          font-size: 0.78rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #5A4A42;
          padding: 8px 4px 2px;
        }
        .mrv-card {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 12px;
          background: #fff;
          border: 1px solid #e8e0d8;
          border-radius: 12px;
          text-align: left;
          cursor: pointer;
          transition: background 0.12s;
        }
        .mrv-card:active { background: #f4ede4; }
        .mrv-card-main { flex: 1; min-width: 0; }
        .mrv-card-title {
          font-size: 0.95rem;
          font-weight: 600;
          color: #3D2518;
          line-height: 1.25;
          word-break: break-word;
        }
        .mrv-card-sub {
          font-size: 0.78rem;
          color: #8b7b73;
          margin-top: 2px;
          word-break: break-word;
        }
        .mrv-card-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-top: 6px;
        }
        .mrv-tag {
          font-size: 0.68rem;
          padding: 2px 8px;
          border-radius: 999px;
          background: #f1e9e1;
          color: #5A4A42;
        }
        .mrv-card-metrics {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
          flex-shrink: 0;
        }
        .mrv-metric { text-align: right; }
        .mrv-metric-value {
          font-size: 0.95rem;
          font-weight: 700;
          color: #3D2518;
          line-height: 1.1;
        }
        .mrv-metric-primary .mrv-metric-value { font-size: 1.05rem; }
        .mrv-metric-label {
          font-size: 0.65rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #8b7b73;
        }
        .mrv-card-chev {
          color: #b8a89e;
          font-size: 1.4rem;
          line-height: 1;
          margin-left: 2px;
        }
        .mrv-empty {
          text-align: center;
          padding: 24px 12px;
          color: #8b7b73;
          font-size: 0.9rem;
          background: #fff;
          border: 1px dashed #d6ccc1;
          border-radius: 10px;
        }
        @media print {
          .mrv { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default MobileReportView;
